import React from 'react';
import { Upload, X, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Renders a list of custom company-info fields driven by an assessment's
 * `company_info_schema.custom_fields` definition. Each field is one of:
 *   text · textarea · number · email · phone · date · dropdown · radio
 *   · multi-select · checkbox · consent · file
 *
 * Props:
 *   fields:   [{key, label, type, required, options?, placeholder?, help?, accept?}]
 *   values:   { [key]: value }
 *   onChange: (key, value) => void
 *   errors:   { [key]: string }
 *   ringClass: tailwind ring class for focused inputs
 *   disabled: boolean (read-only mode)
 */
export default function CustomFieldsRenderer({ fields = [], values = {}, onChange, errors = {}, ringClass = 'focus:ring-emerald-500', disabled = false }) {
  if (!fields || fields.length === 0) return null;

  const inputCls = (hasError) =>
    `w-full bg-white border rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 outline-none focus:ring-2 ${ringClass} transition-all ${hasError ? 'border-red-400' : 'border-gray-300'} ${disabled ? 'bg-gray-50 cursor-not-allowed' : ''}`;

  const handleFile = async (key, file) => {
    if (!file) { onChange(key, null); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/upload`, { method: 'POST', body: fd });
      if (res.ok) {
        const data = await res.json();
        onChange(key, { id: data.id, filename: data.filename });
      }
    } catch (e) { console.error('file upload failed', e); }
  };

  return (
    <div className="space-y-5">
      {fields.map((f) => {
        const v = values[f.key];
        const err = errors[f.key];
        const baseLabel = (
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {f.label}{f.required ? ' *' : ''}
          </label>
        );
        const helpText = f.help ? <p className="text-xs text-gray-500 mt-1">{f.help}</p> : null;
        const errText = err ? <p className="text-red-500 text-xs mt-1">{err}</p> : null;

        switch ((f.type || 'text').toLowerCase()) {
          case 'textarea':
            return (
              <div key={f.key} data-testid={`custom-field-${f.key}`}>
                {baseLabel}
                <textarea rows={3} value={v || ''} disabled={disabled} placeholder={f.placeholder || ''}
                  onChange={(e) => onChange(f.key, e.target.value)}
                  className={`${inputCls(!!err)} resize-none`} />
                {helpText}{errText}
              </div>
            );
          case 'number':
            return (
              <div key={f.key} data-testid={`custom-field-${f.key}`}>
                {baseLabel}
                <input type="number" value={v ?? ''} disabled={disabled} placeholder={f.placeholder || ''}
                  onChange={(e) => onChange(f.key, e.target.value === '' ? null : Number(e.target.value))}
                  className={inputCls(!!err)} />
                {helpText}{errText}
              </div>
            );
          case 'email':
          case 'phone':
            return (
              <div key={f.key} data-testid={`custom-field-${f.key}`}>
                {baseLabel}
                <input type={f.type === 'email' ? 'email' : 'tel'} value={v || ''} disabled={disabled}
                  placeholder={f.placeholder || ''}
                  onChange={(e) => onChange(f.key, e.target.value)}
                  className={inputCls(!!err)} />
                {helpText}{errText}
              </div>
            );
          case 'date':
            return (
              <div key={f.key} data-testid={`custom-field-${f.key}`}>
                {baseLabel}
                <input type="date" value={v || ''} disabled={disabled}
                  onChange={(e) => onChange(f.key, e.target.value)}
                  className={inputCls(!!err)} />
                {helpText}{errText}
              </div>
            );
          case 'dropdown':
          case 'select': {
            const opts = f.options || [];
            return (
              <div key={f.key} data-testid={`custom-field-${f.key}`}>
                {baseLabel}
                <select value={v || ''} disabled={disabled} onChange={(e) => onChange(f.key, e.target.value)}
                  className={inputCls(!!err)}>
                  <option value="">— select —</option>
                  {opts.map((o) => (
                    <option key={typeof o === 'string' ? o : o.value || o.label} value={typeof o === 'string' ? o : (o.value || o.label)}>
                      {typeof o === 'string' ? o : (o.label || o.value)}
                    </option>
                  ))}
                </select>
                {helpText}{errText}
              </div>
            );
          }
          case 'radio': {
            const opts = f.options || [];
            return (
              <div key={f.key} data-testid={`custom-field-${f.key}`}>
                {baseLabel}
                <div className="space-y-2">
                  {opts.map((o) => {
                    const val = typeof o === 'string' ? o : (o.value || o.label);
                    const lbl = typeof o === 'string' ? o : (o.label || o.value);
                    return (
                      <label key={val} className="flex items-center gap-3 text-sm text-gray-700 cursor-pointer">
                        <input type="radio" name={f.key} value={val} checked={v === val} disabled={disabled}
                          onChange={() => onChange(f.key, val)}
                          className="text-emerald-600" />
                        <span>{lbl}</span>
                      </label>
                    );
                  })}
                </div>
                {helpText}{errText}
              </div>
            );
          }
          case 'multi-select':
          case 'multiselect': {
            const opts = f.options || [];
            const arr = Array.isArray(v) ? v : [];
            const toggle = (val) => {
              const next = arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val];
              onChange(f.key, next);
            };
            return (
              <div key={f.key} data-testid={`custom-field-${f.key}`}>
                {baseLabel}
                <div className="flex flex-wrap gap-2">
                  {opts.map((o) => {
                    const val = typeof o === 'string' ? o : (o.value || o.label);
                    const lbl = typeof o === 'string' ? o : (o.label || o.value);
                    const active = arr.includes(val);
                    return (
                      <button key={val} type="button" disabled={disabled}
                        onClick={() => toggle(val)}
                        className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${active ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
                        {lbl}
                      </button>
                    );
                  })}
                </div>
                {helpText}{errText}
              </div>
            );
          }
          case 'checkbox':
          case 'consent':
            return (
              <div key={f.key} data-testid={`custom-field-${f.key}`}>
                <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={!!v} disabled={disabled}
                    onChange={(e) => onChange(f.key, e.target.checked)}
                    className="mt-0.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                  <span>
                    {f.label}{f.required ? ' *' : ''}
                    {f.help && <span className="block text-xs text-gray-500 mt-0.5">{f.help}</span>}
                  </span>
                </label>
                {errText}
              </div>
            );
          case 'file':
            return (
              <div key={f.key} data-testid={`custom-field-${f.key}`}>
                {baseLabel}
                {v && v.id ? (
                  <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    <FileText className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm text-emerald-800 flex-1 truncate">{v.filename}</span>
                    {!disabled && (
                      <button type="button" onClick={() => onChange(f.key, null)} className="text-red-400 hover:text-red-600">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ) : (
                  <label className={`inline-flex items-center gap-2 text-sm border border-dashed border-gray-300 rounded-lg px-4 py-3 cursor-pointer hover:bg-gray-50 ${disabled ? 'pointer-events-none opacity-50' : ''}`}>
                    <Upload className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Choose file</span>
                    <input type="file" className="hidden" accept={f.accept || '*'}
                      onChange={(e) => handleFile(f.key, e.target.files?.[0])} />
                  </label>
                )}
                {helpText}{errText}
              </div>
            );
          default: // 'text'
            return (
              <div key={f.key} data-testid={`custom-field-${f.key}`}>
                {baseLabel}
                <input type="text" value={v || ''} disabled={disabled} placeholder={f.placeholder || ''}
                  onChange={(e) => onChange(f.key, e.target.value)}
                  className={inputCls(!!err)} />
                {helpText}{errText}
              </div>
            );
        }
      })}
    </div>
  );
}

// Helper: validate field values against a custom-fields schema. Returns {error_map, ok}.
export function validateCustomFields(fields, values) {
  const errs = {};
  for (const f of fields || []) {
    if (!f.required) continue;
    const v = values[f.key];
    const t = (f.type || 'text').toLowerCase();
    if (t === 'consent' || t === 'checkbox') {
      if (!v) errs[f.key] = 'Required';
    } else if (Array.isArray(v)) {
      if (v.length === 0) errs[f.key] = 'Required';
    } else if (v === undefined || v === null || (typeof v === 'string' && !v.trim())) {
      errs[f.key] = 'Required';
    }
  }
  return { errors: errs, ok: Object.keys(errs).length === 0 };
}
